import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { bankingApi } from '../../api/banking';
import type { BankOperationDto, CreateBankOperationRequest } from '../../api/banking';
import { accountsApi } from '../../api/accounts';
import { personsApi } from '../../api/persons';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate, monthsAgo, today } from '../../utils/formatDate';
import DataTable from '../../components/DataTable';
import DatePickerInput from '../../components/DatePickerInput';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';
import { useToast } from '../../components/Toast';
import { formatApiError } from '../../utils/errors';
import { useReportScreen } from '../../components/ScreenContext';
import { useDocumentSerialPreview } from '../../hooks/useDocumentSerial';
import { useAppBack, useBeforeUnload } from '../../hooks/useAppBack';

export default function BankingList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [from, setFrom] = useState(monthsAgo(3));
  const [to, setTo] = useState(today());
  const [showForm, setShowForm] = useState(false);
  const [selectedOp, setSelectedOp] = useState<BankOperationDto | null>(null);
  // Finding 35: the payment card is unsaved work — the browser's Back must
  // close the CARD (with a confirm when dirty), never fall through the app.
  const [formDirty, setFormDirty] = useState(false);

  const closeOperationForm = () => {
    setShowForm(false);
    setSelectedOp(null);
    setFormDirty(false);
  };

  useAppBack({
    enabled: showForm,
    blocking: formDirty,
    goBack: () => {
      closeOperationForm();
      return false; // one press closes the card — it consumed the sentinel
    },
    onConfirmLeave: closeOperationForm,
  });
  useBeforeUnload(formDirty);
  // Inline note editor for the "Rankinis derinimas" marker (one row at a time)
  const [matchNoteEditor, setMatchNoteEditor] = useState<{ id: number; note: string } | null>(null);

  const { data: operations, isLoading } = useQuery({
    queryKey: ['bank-operations', from, to],
    queryFn: () => bankingApi.list({ from, to }),
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });

  const { data: persons } = useQuery({
    queryKey: ['persons-banking'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => bankingApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['bank-operations'] });
      // Finding 18: deleting an operation changes the Darbai work items —
      // refresh the attention-inbox counters the board badges derive from.
      queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      addToast(t('banking.operationDeleted', { id }), 'success');
    },
    onError: (err: Error) => addToast(formatApiError(err), 'error'),
  });

  // "Rankinis derinimas" marker set/clear — marking also drops the payment's
  // automation-created settlement links, so the Darbai attention inbox
  // content changes with it (invalidate its queries too).
  const manualMatchMutation = useMutation({
    mutationFn: (action: { id: number; mode: 'set' | 'clear'; note?: string }) =>
      action.mode === 'set'
        ? bankingApi.setManualMatch(action.id, action.note)
        : bankingApi.clearManualMatch(action.id),
    onSuccess: (_data, action) => {
      queryClient.invalidateQueries({ queryKey: ['bank-operations'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      setMatchNoteEditor(null);
      addToast(t(action.mode === 'set' ? 'banking.manualMatchMarked' : 'banking.manualMatchCleared'), 'success');
    },
    onError: (err: Error) => addToast(formatApiError(err), 'error'),
  });

  // All hooks must be above any early return (Rules of Hooks)
  const accountMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of accounts ?? []) {
      map.set(a.accountNumber, a.name);
    }
    return map;
  }, [accounts]);

  const personMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of persons ?? []) {
      map.set(p.id, p.name);
    }
    return map;
  }, [persons]);

  const opCount = operations?.length ?? 0;
  const screenFilters = useMemo(() => ({ nuo: from, iki: to }), [from, to]);
  const screenPreview = useMemo(() =>
    (operations ?? []).slice(0, 8).map(op => {
      const personName = personMap.get(op.personId ?? 0) ?? 'Nezinomas';
      const debit = op.accountDebit > 0 ? `D=${op.accountDebit}` : '';
      const credit = op.accountCredit > 0 ? `C=${op.accountCredit}` : '';
      return `${op.uniqueNumber || op.id}: ${formatMoney(op.uniqueSum)} ${debit}${credit} Asmuo: ${personName} (${formatDate(op.insertDate)})`;
    }), [operations, personMap]);

  useReportScreen(
    selectedOp
      ? `Bankines operacijos (${opCount} irasu). Pasirinkta: ${selectedOp.uniqueNumber || selectedOp.id}, suma ${formatMoney(selectedOp.uniqueSum)}, saskaita D=${selectedOp.accountDebit} C=${selectedOp.accountCredit}`
      : `Bankines operacijos (${opCount} irasu)`,
    {
      recordCount: opCount,
      filters: screenFilters,
      preview: screenPreview,
      activeRecord: selectedOp ? {
        type: 'bank-operation',
        fields: {
          'Numeris': selectedOp.uniqueNumber || String(selectedOp.id),
          'Suma': `${formatMoney(selectedOp.uniqueSum)} ${selectedOp.currencyCode}`,
          'Debeto saskaita': String(selectedOp.accountDebit),
          'Kredito saskaita': String(selectedOp.accountCredit),
          'Data': formatDate(selectedOp.insertDate),
          'Banko saskaita': selectedOp.bankAccountCode,
          'Asmuo': personMap.get(selectedOp.personId ?? 0) ?? 'Nepriskirta',
        },
      } : undefined,
    }
  );

  if (isLoading) return <LoadingSpinner />;

  // Compute summary stats
  const totalInflow = (operations ?? []).reduce((sum, op) => sum + (op.accountCredit > 0 ? op.uniqueSum : 0), 0);
  const totalOutflow = (operations ?? []).reduce((sum, op) => sum + (op.accountDebit > 0 ? op.uniqueSum : 0), 0);
  const netCash = totalInflow - totalOutflow;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">{t('banking.title')}</h1>
        <div className="flex gap-2">
          <button
            className="px-4 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100"
            onClick={() => navigate('/banking/accounts')}
          >
            {t('banking.manageAccounts')}
          </button>
          <button
            className="px-4 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100"
            onClick={() => navigate('/banking/import')}
          >
            {t('banking.importStatement')}
          </button>
          <button
            className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            onClick={() => { setShowForm(true); setSelectedOp(null); setFormDirty(false); }}
          >
            {t('banking.newOperation')}
          </button>
        </div>
      </div>

      {/* Cash Position Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">{t('banking.inflowCredit')}</div>
          <div className="text-lg font-semibold text-green-600 num"><MoneyCell value={totalInflow} /></div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">{t('banking.outflowDebit')}</div>
          <div className="text-lg font-semibold text-red-600 num"><MoneyCell value={totalOutflow} /></div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">{t('banking.netCashMovement')}</div>
          <div className={`text-lg font-semibold num ${netCash >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            <MoneyCell value={netCash} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <DatePickerInput
          label={t('common.from')}
          value={from}
          onChange={setFrom}
          inputClassName="w-36"
        />
        <DatePickerInput
          label={t('common.to')}
          value={to}
          onChange={setTo}
          inputClassName="w-36"
        />
      </div>

      {/* Create/Edit form */}
      {showForm && (
        <BankOperationForm
          operation={selectedOp}
          accounts={accounts ?? []}
          persons={persons ?? []}
          t={t}
          onDirtyChange={setFormDirty}
          onSubmit={async (data) => {
            if (selectedOp) {
              await bankingApi.update(selectedOp.id, data);
            } else {
              await bankingApi.create(data);
            }
            queryClient.invalidateQueries({ queryKey: ['bank-operations'] });
            // Finding 18: a created/edited operation changes the Darbai work
            // items — refresh the attention-inbox counters too.
            queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
            closeOperationForm();
          }}
          onCancel={closeOperationForm}
        />
      )}

      {/* Operations table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <DataTable
          columns={[
            {
              key: 'insertDate',
              header: t('common.date'),
              render: (row) => formatDate(row.insertDate),
              className: 'whitespace-nowrap',
            },
            { key: 'uniqueNumber', header: t('common.docNumber'), className: 'whitespace-nowrap' },
            {
              key: 'accountDebit',
              header: t('banking.debitAcct'),
              render: (row) => {
                const name = accountMap.get(row.accountDebit);
                return name ? `${row.accountDebit} ${name}` : String(row.accountDebit || '-');
              },
              className: 'max-w-40 truncate',
            },
            {
              key: 'accountCredit',
              header: t('banking.creditAcct'),
              render: (row) => {
                const name = accountMap.get(row.accountCredit);
                return name ? `${row.accountCredit} ${name}` : String(row.accountCredit || '-');
              },
              className: 'max-w-40 truncate',
            },
            {
              key: 'uniqueSum',
              header: t('common.amount'),
              render: (row) => <span className="num">{formatMoney(row.uniqueSum)}</span>,
              className: 'text-right',
            },
            {
              key: 'currencyCode',
              header: t('common.currency'),
              className: 'text-center',
            },
            {
              key: 'personId',
              header: t('common.person'),
              render: (row) => row.personId ? (personMap.get(row.personId) ?? `#${row.personId}`) : '-',
              className: 'max-w-32 truncate',
            },
            {
              key: 'manualMatch',
              header: t('banking.manualMatch'),
              render: (row) => {
                // Inline note editor for this row
                if (matchNoteEditor?.id === row.id) {
                  return (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        maxLength={200}
                        autoFocus
                        className="w-40 px-2 py-0.5 text-xs border border-amber-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-400"
                        placeholder={t('banking.manualMatchNotePrompt')}
                        value={matchNoteEditor.note}
                        onChange={(e) => setMatchNoteEditor({ id: row.id, note: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            manualMatchMutation.mutate({ id: row.id, mode: 'set', note: matchNoteEditor.note });
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            setMatchNoteEditor(null);
                          }
                        }}
                      />
                      <button
                        className="text-xs font-medium text-amber-700 hover:underline disabled:opacity-50"
                        disabled={manualMatchMutation.isPending}
                        onClick={() => manualMatchMutation.mutate({ id: row.id, mode: 'set', note: matchNoteEditor.note })}
                      >
                        {t('common.confirm')}
                      </button>
                      <button
                        className="text-xs text-gray-400 hover:underline"
                        onClick={() => setMatchNoteEditor(null)}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  );
                }
                if (row.manualMatch) {
                  return (
                    <div className="flex flex-col items-start gap-0.5">
                      <span
                        className="inline-block px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                        title={row.manualMatchNote ?? t('banking.manualMatchHint')}
                      >
                        {t('banking.manualMatch')}
                      </span>
                      {row.manualMatchNote && (
                        <span className="max-w-40 truncate text-[11px] text-amber-700" title={row.manualMatchNote}>
                          {row.manualMatchNote}
                        </span>
                      )}
                      <button
                        className="text-[11px] text-gray-500 hover:underline disabled:opacity-50"
                        disabled={manualMatchMutation.isPending}
                        title={t('banking.manualMatchHint')}
                        onClick={(e) => { e.stopPropagation(); manualMatchMutation.mutate({ id: row.id, mode: 'clear' }); }}
                      >
                        {t('banking.manualMatchRemove')}
                      </button>
                    </div>
                  );
                }
                return (
                  <button
                    className="px-2 py-0.5 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100"
                    title={t('banking.manualMatchHint')}
                    onClick={(e) => { e.stopPropagation(); setMatchNoteEditor({ id: row.id, note: '' }); }}
                  >
                    {t('banking.manualMatchMark')}
                  </button>
                );
              },
              className: 'whitespace-nowrap',
            },
            {
              key: 'actions',
              header: '',
              render: (row) => (
                <div className="flex gap-2 justify-end">
                  <button
                    className="text-xs text-primary-600 hover:underline"
                    onClick={(e) => { e.stopPropagation(); setSelectedOp(row); setShowForm(true); setFormDirty(false); }}
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    className="text-xs text-red-500 hover:underline"
                    onClick={(e) => { e.stopPropagation(); if (confirm(t('banking.deleteOperation') + ` #${row.id}?`)) deleteMutation.mutate(row.id); }}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              ),
              className: 'text-right',
            },
          ]}
          data={operations ?? []}
          keyFn={(row) => row.id}
          emptyMessage={t('banking.noBankOperations')}
        />
      </div>

      {/* Reconciliation Workspace */}
      <ReconciliationWorkspace t={t} personMap={personMap} />
    </div>
  );
}

function BankOperationForm({ operation, accounts, persons, onSubmit, onCancel, onDirtyChange, t }: {
  operation: BankOperationDto | null;
  accounts: { accountNumber: number; name: string }[];
  persons: { id: number; name: string }[];
  onSubmit: (data: CreateBankOperationRequest) => Promise<void>;
  onCancel: () => void;
  /** Finding 35: fires true on the first USER edit (drives the back-guard). */
  onDirtyChange: (dirty: boolean) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const [form, setForm] = useState<CreateBankOperationRequest>({
    bankAccountCode: operation?.bankAccountCode ?? '',
    currencyCode: operation?.currencyCode ?? 'EUR',
    currencyRate: operation?.currencyRate ?? 1,
    uniqueSum: operation?.uniqueSum ?? 0,
    uniqueNumber: operation?.uniqueNumber ?? '',
    accountOperationCode: '',
    accountDebit: operation?.accountDebit ?? 0,
    accountCredit: operation?.accountCredit ?? 0,
    personId: operation?.personId ?? null,
    operationDate: operation?.insertDate ?? today(),
  });
  const [saving, setSaving] = useState(false);

  // USER edits go through update() (mark dirty); programmatic fills (the
  // auto-number effect below) use setForm directly so they never count.
  const update = (patch: Partial<CreateBankOperationRequest>) => {
    setForm(f => ({ ...f, ...patch }));
    onDirtyChange(true);
  };

  // Auto-numbering from document serial
  const { previewNumber, autoSerialCode, autoFilled, setAutoFilled } = useDocumentSerialPreview(
    'BankOperation', form.operationDate
  );

  // Auto-fill document number from serial when no explicit number set
  useEffect(() => {
    if (!operation && previewNumber && !form.uniqueNumber && !autoFilled) {
      setForm(f => ({ ...f, uniqueNumber: previewNumber }));
      setAutoFilled(true);
    }
  }, [previewNumber, operation, form.uniqueNumber, autoFilled, setAutoFilled]);

  // Filter accounts to show bank accounts (class 2xx typically)
  const bankAccounts = accounts.filter(a =>
    String(a.accountNumber).startsWith('2') || String(a.accountNumber).startsWith('24')
  );

  const handleSubmit = async () => {
    setSaving(true);
    const explicitNumber = form.uniqueNumber.trim();
    try {
      await onSubmit({
        ...form,
        uniqueNumber: explicitNumber || '',
        autoSerialCode: !explicitNumber && autoSerialCode ? autoSerialCode : undefined,
      });
    } catch (err) { alert(err instanceof Error ? err.message : 'Error'); }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-3">
        {operation ? t('banking.editBankOperation') : t('banking.newBankOperation')}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.documentNumberLabel')}</label>
          <input
            type="text"
            className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md ${!form.uniqueNumber && previewNumber ? 'placeholder:text-gray-400' : ''}`}
            value={form.uniqueNumber}
            onChange={(e) => update({ uniqueNumber: e.target.value })}
            placeholder={previewNumber || t('banking.docNumberPlaceholder')}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.amountLabel')}</label>
          <input
            type="number"
            step="0.01"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.uniqueSum || ''}
            onChange={(e) => update({ uniqueSum: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.debitAccountLabel')}</label>
          <select
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.accountDebit}
            onChange={(e) => update({ accountDebit: parseInt(e.target.value) || 0 })}
          >
            <option value={0}>{t('banking.selectAccount')}</option>
            {accounts.map(a => (
              <option key={a.accountNumber} value={a.accountNumber}>
                {a.accountNumber} {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.creditAccountLabel')}</label>
          <select
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.accountCredit}
            onChange={(e) => update({ accountCredit: parseInt(e.target.value) || 0 })}
          >
            <option value={0}>{t('banking.selectAccount')}</option>
            {accounts.map(a => (
              <option key={a.accountNumber} value={a.accountNumber}>
                {a.accountNumber} {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.bankAccountCodeLabel')}</label>
          <select
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.bankAccountCode}
            onChange={(e) => update({ bankAccountCode: e.target.value })}
          >
            <option value="">{t('banking.selectAccount')}</option>
            {bankAccounts.map(a => (
              <option key={a.accountNumber} value={String(a.accountNumber)}>
                {a.accountNumber} {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.personLabel')}</label>
          <select
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.personId ?? ''}
            onChange={(e) => update({ personId: e.target.value ? parseInt(e.target.value) : null })}
          >
            <option value="">{t('banking.selectPerson')}</option>
            {persons.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.currencyLabel')}</label>
          <select
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.currencyCode ?? 'EUR'}
            onChange={(e) => update({ currencyCode: e.target.value })}
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('banking.currencyRateLabel')}</label>
          <input
            type="number"
            step="0.0001"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.currencyRate || ''}
            onChange={(e) => update({ currencyRate: parseFloat(e.target.value) || 1 })}
          />
        </div>
        <div className="flex items-end gap-2 col-span-2">
          <button
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            onClick={handleSubmit}
            disabled={saving || (!form.uniqueNumber && !autoSerialCode) || form.uniqueSum <= 0}
          >
            {saving ? t('common.saving') : (operation ? t('banking.updateOperation') : t('banking.createOperation'))}
          </button>
          <button
            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReconciliationWorkspace({ t, personMap }: {
  t: (key: string, opts?: Record<string, unknown>) => string;
  personMap: Map<number, string>;
}) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [showUnreconciled, setShowUnreconciled] = useState(false);
  const [from, setFrom] = useState(monthsAgo(3));
  const [to, setTo] = useState(today());

  const { data: unreconciled, isLoading } = useQuery({
    queryKey: ['unreconciled-operations', from, to],
    queryFn: () => bankingApi.unreconciled({ from, to }),
    enabled: showUnreconciled,
  });

  const reconcileMutation = useMutation({
    mutationFn: ({ id, isReconciled }: { id: number; isReconciled: boolean }) =>
      bankingApi.toggleReconciliation(id, isReconciled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unreconciled-operations'] });
      queryClient.invalidateQueries({ queryKey: ['bank-operations'] });
      // Finding 18: a toggled reconciliation removes/adds a Darbai work item —
      // refresh the attention counters AND the dashboard tiles with them.
      queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-overview'] });
      addToast(t('banking.reconciliationUpdated'), 'success');
    },
    onError: (err: Error) => addToast(formatApiError(err), 'error'),
  });

  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-gray-700">{t('banking.reconciliationWorkspace')}</h2>
        <button
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            showUnreconciled
              ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-100'
          }`}
          onClick={() => setShowUnreconciled(!showUnreconciled)}
        >
          {showUnreconciled ? t('banking.hideUnreconciled') : t('banking.showUnreconciled')}
        </button>
      </div>

      {showUnreconciled && (
        <div className="space-y-3">
          <div className="flex gap-3 items-end">
            <DatePickerInput
              label={t('common.from')}
              value={from}
              onChange={setFrom}
              inputClassName="w-36"
            />
            <DatePickerInput
              label={t('common.to')}
              value={to}
              onChange={setTo}
              inputClassName="w-36"
            />
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : unreconciled && unreconciled.length > 0 ? (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="px-3 py-2 border-b border-gray-100 bg-amber-50">
                <span className="text-xs font-medium text-amber-700">
                  {unreconciled.length} {t('banking.unreconciledCount')}
                </span>
              </div>
              <DataTable
                columns={[
                  {
                    key: 'insertDate',
                    header: t('common.date'),
                    render: (row) => formatDate(row.insertDate),
                    className: 'whitespace-nowrap',
                  },
                  { key: 'uniqueNumber', header: t('common.docNumber'), className: 'whitespace-nowrap' },
                  {
                    key: 'uniqueSum',
                    header: t('common.amount'),
                    render: (row) => <span className="num">{formatMoney(row.uniqueSum)}</span>,
                    className: 'text-right',
                  },
                  {
                    key: 'currencyCode',
                    header: t('common.currency'),
                    className: 'text-center',
                  },
                  {
                    key: 'personId',
                    header: t('common.person'),
                    render: (row) => row.personId ? (personMap.get(row.personId) ?? `#${row.personId}`) : '-',
                    className: 'max-w-32 truncate',
                  },
                  {
                    key: 'actions',
                    header: '',
                    render: (row) => (
                      <div className="flex justify-end">
                        <button
                          className="text-xs font-medium text-green-600 hover:text-green-800 hover:underline"
                          onClick={(e) => { e.stopPropagation(); reconcileMutation.mutate({ id: row.id, isReconciled: true }); }}
                          disabled={reconcileMutation.isPending}
                        >
                          {t('banking.markReconciled')}
                        </button>
                      </div>
                    ),
                    className: 'text-right',
                  },
                ]}
                data={unreconciled}
                keyFn={(row) => row.id}
                emptyMessage={t('banking.allReconciled')}
              />
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-green-600">
              {t('banking.allReconciled')}
            </div>
          )}
        </div>
      )}

      {!showUnreconciled && (
        <p className="text-xs text-gray-500">
          {t('banking.reconciliationDescription')}
        </p>
      )}
    </div>
  );
}
